import { Liquid } from 'liquidjs';

import {
	HolderRole,
	SmartFieldsAvailableSources,
} from '@dealstack/shared/lib/common/business/smartFields';
import {
	InvestmentWorkflowType,
	TransactionTitle,
	TransactionType,
} from '@dealstack/shared/lib/generated';
import { numberToWords } from '@dealstack/shared/lib/format/numberToWords';
import { formatNumber as formatNumberValue } from '@dealstack/shared/lib/format/number';

export enum LiquidJsCustomFilters {
	GET_CUSTOM_DETAIL_VALUE = 'getCustomDetailValue',
	MINUS_YEAR = 'minus_year',
	NUMBER = 'number',
	CURRENCY = 'currency',
	YESNO = 'yesno',
	CHECKBOX = 'checkbox',
	WORDS = 'words',
	TRANSACTION_TYPE = 'transaction_type',
	TRANSACTION_TITLE = 'transaction_title',
	INVESTMENT_TYPE = 'investment_type',
	HOLDER_ROLE = 'holder_role',
}

const LOCALE = 'en-GB';

// Narrows to a finite number without throwing or producing NaN.
export const toFiniteNumber = (value: unknown): number | undefined => {
	if (typeof value === 'number') {
		return Number.isFinite(value) ? value : undefined;
	}
	if (typeof value === 'string' && value.trim() !== '') {
		const parsed = Number(value);
		return Number.isFinite(parsed) ? parsed : undefined;
	}
	return undefined;
};

// Intl throws a RangeError outside [0, 20].
const clampFractionDigits = (value: number): number => Math.min(Math.max(Math.trunc(value), 0), 20);

/**
 * The `number` filter, rendering through the platform's shared formatter.
 *
 * The three conventions a document does not share with the cap-table UI are passed explicitly:
 * a minus sign rather than accounting parentheses, a numeric zero rather than a dash, and the
 * document locale. Everything else — the min/max precision range, the grouping — is the UI's,
 * so a figure in a deed matches the figure on the screen it came from.
 */
const formatNumber = (
	value: unknown,
	minimumFractionDigits?: unknown,
	maximumFractionDigits?: unknown
): string => {
	const num = toFiniteNumber(value);
	if (num === undefined) {
		return '';
	}

	const minFracArg = toFiniteNumber(minimumFractionDigits);
	const maxFracArg = toFiniteNumber(maximumFractionDigits);

	const minFrac = minFracArg === undefined ? undefined : clampFractionDigits(minFracArg);
	const maxFracRequested = maxFracArg === undefined ? 2 : clampFractionDigits(maxFracArg);
	// Intl requires max >= min, so widen max when only min was supplied.
	const maxFrac = Math.max(maxFracRequested, minFrac ?? 0);

	return formatNumberValue({
		value: num,
		// Trailing zeros are padded only where the author asked for a minimum.
		lockPrecision: minFrac !== undefined,
		minPrecision: minFrac ?? 0,
		maxPrecision: maxFrac,
		returnNumericalZero: true,
		negativeStyle: 'minus',
		locale: LOCALE,
	});
};

const formatYesNo = (value: unknown, yes: unknown = 'Yes', no: unknown = 'No'): string => {
	if (value === true) {
		return String(yes);
	} else if (value === false) {
		return String(no);
	} else {
		return '';
	}
};

/**
 * Document-facing labels for the transaction enums. Keyed by the Prisma enum member;
 * ExpectedTransactionType's four members are name-identical to their TransactionType
 * counterparts, so this one map serves both.
 *
 * Typed against the enum rather than `Record<string, string>` so a member added to the schema
 * cannot silently reach a generated document as its RAW value — `REDEMPTION_REVERSE_SPLIT` in a
 * deed rather than "Redemption (reverse split)". `labelFrom`'s raw-value fallback stays as the
 * runtime safety net for data that predates a label, but it is no longer the first line of
 * defence.
 *
 * These are deliberately hardcoded English, not i18n keys: documents are generated server-side
 * with no translation context, as every catalogue label is. The UI has its own translated map in
 * `getTransactionTypeText`, and the two are structurally pinned together by this module's spec.
 */
const TRANSACTION_TYPE_LABELS: Record<TransactionType, string> = {
	TRANSFER: 'Transfer',
	ISSUE: 'Issue',
	REDEMPTION: 'Redemption',
	CANCELLATION: 'Cancellation',
	ISSUE_SPLIT: 'Issue (split)',
	REDEMPTION_REVERSE_SPLIT: 'Redemption (reverse split)',
	TRANSFER_CONVERSION: 'Transfer (conversion)',
	SHAREHOLDER_CONTRIBUTION: 'Shareholder contribution',
	REDUCTION_IN_SHARE_CAPITAL: 'Reduction in share capital',
	CONVERSION_SHARE_CAPITAL: 'Conversion of share capital',
	DIVIDEND: 'Dividend',
	VESTING_EVENT: 'Vesting event',
};

const TRANSACTION_TITLE_LABELS: Record<TransactionTitle, string> = {
	LEGAL_TITLE: 'Legal title',
	BENEFICIAL_INTEREST: 'Beneficial interest',
	BENEFICIAL_INTEREST_AND_LEGAL_TITLE: 'Beneficial interest and legal title',
};

const INVESTMENT_TYPE_LABELS: Record<InvestmentWorkflowType, string> = {
	JOINER_CREATE: 'New joiner',
	JOINER: 'Joiner',
	ALLOCATION: 'Allocation',
	LEAVER: 'Leaver',
};

const HOLDER_ROLE_LABELS: Record<HolderRole, string> = {
	[HolderRole.buyer]: 'Buyer',
	[HolderRole.seller]: 'Seller',
};

// A null/absent enum renders blank (the field simply has no value); an
// UNKNOWN one renders its raw value, so a member added to the schema without a
// label here is visible rather than silently missing.
const labelFrom =
	(labels: Record<string, string>) =>
	(value: unknown): string => {
		if (typeof value !== 'string' || value === '') {
			return '';
		}
		return labels[value] ?? value;
	};

const CHECK_MARK = '✓';

// AcroForm "Off"/empty/falsy strings render blank; anything else ticks.
const formatCheckbox = (value: unknown): string => {
	if (value === true) {
		return CHECK_MARK;
	}
	if (typeof value === 'number') {
		return value !== 0 ? CHECK_MARK : '';
	}
	if (typeof value === 'string') {
		const v = value.trim().toLowerCase();
		if (
			v === '' ||
			v === 'off' ||
			v === 'false' ||
			v === 'no' ||
			v === '0' ||
			v === 'unchecked'
		) {
			return '';
		}
		return CHECK_MARK;
	}
	return '';
};

const formatCurrency = (value: unknown, currencyCode: unknown): string => {
	const num = toFiniteNumber(value);
	if (num === undefined) {
		return '';
	}

	const code = typeof currencyCode === 'string' ? currencyCode.trim() : '';
	if (code === '') {
		// No code to resolve a symbol from; keep the monetary shape.
		return formatNumber(value, 2, 2);
	}

	try {
		return new Intl.NumberFormat(LOCALE, {
			style: 'currency',
			currency: code,
		}).format(num);
	} catch {
		// Malformed ISO code — Intl throws. Filters must never throw.
		return formatNumber(value, 2, 2);
	}
};

// SECURITY: template content here is author-controlled, and LiquidJS's
// include/layout/render tags read from disk relative to `root` (default: CWD).
// Unrestricted, `{% include "some/server/file" %}` is a local-file-include.
// `root: []` denies every lookup at the loader level; these no-op tag overrides
// are belt-and-braces in case anything ever calls renderFile with a real root.
const disableFileInclude = (engine: Liquid): void => {
	const noopFileTag = {
		// Nothing to resolve, so the tag's arguments are never parsed.
		parse: (): void => undefined,
		// Renders as if the tag were absent.
		render: (): string => '',
	};

	engine.registerTag('include', noopFileTag);
	engine.registerTag('layout', noopFileTag);
	engine.registerTag('render', noopFileTag);
};

export const initializeLiquidJsEngine = (): Liquid => {
	const engine = new Liquid({
		//https://liquidjs.com/tutorials/dos.html#TL-DR
		parseLimit: 1e8,
		renderLimit: 1000,
		memoryLimit: 1e9,
		// See disableFileInclude: closes the LFI at the loader level too.
		root: [],
	});

	disableFileInclude(engine);

	engine.registerFilter(
		LiquidJsCustomFilters.GET_CUSTOM_DETAIL_VALUE,
		(array: SmartFieldsAvailableSources['customDetails'], customDetailDefinitionId: string) => {
			if (!Array.isArray(array)) {
				return null;
			}
			return (
				array.find((customDetail) => customDetail.id === customDetailDefinitionId)
					?.answer ?? null
			);
		}
	);

	engine.registerFilter('minus_year', (currentYear) => {
		return parseInt(currentYear) - 1;
	});

	engine.registerFilter(LiquidJsCustomFilters.NUMBER, formatNumber);

	engine.registerFilter(LiquidJsCustomFilters.CURRENCY, formatCurrency);

	engine.registerFilter(LiquidJsCustomFilters.YESNO, formatYesNo);

	engine.registerFilter(LiquidJsCustomFilters.CHECKBOX, formatCheckbox);

	// Deeds state a quantity in figures and in words side by side so the two can be checked
	// against each other: `{{ shares | number }} ({{ shares | words }})`.
	engine.registerFilter(LiquidJsCustomFilters.WORDS, numberToWords);

	// Transaction/investment enums are stored as Prisma members but read by
	// humans in a legal document: `REDUCTION_IN_SHARE_CAPITAL` is not a sentence.
	engine.registerFilter(
		LiquidJsCustomFilters.TRANSACTION_TYPE,
		labelFrom(TRANSACTION_TYPE_LABELS)
	);
	engine.registerFilter(
		LiquidJsCustomFilters.TRANSACTION_TITLE,
		labelFrom(TRANSACTION_TITLE_LABELS)
	);
	engine.registerFilter(LiquidJsCustomFilters.INVESTMENT_TYPE, labelFrom(INVESTMENT_TYPE_LABELS));
	engine.registerFilter(LiquidJsCustomFilters.HOLDER_ROLE, labelFrom(HOLDER_ROLE_LABELS));

	return engine;
};

export const NATIVE_LIQUID_JS_FILTERS = [
	'abs',
	'append',
	'capitalize',
	'ceil',
	'date',
	'default',
	'divided_by',
	'downcase',
	'escape',
	'first',
	'floor',
	'join',
	'last',
	'lstrip',
	'map',
	'minus',
	'modulo',
	'newline_to_br',
	'plus',
	'prepend',
	'remove',
	'remove_first',
	'replace',
	'replace_first',
	'reverse',
	'round',
	'rstrip',
	'size',
	'slice',
	'sort',
	'split',
	'strip',
	'strip_html',
	'strip_newlines',
	'times',
	'truncate',
	'truncatewords',
	'uniq',
	'upcase',
	'url_encode',
	'at_least',
	'at_most',
	'compact',
	'concat',
	'filled',
	'singleton',
	'with',
	'tap',
	'for',
	'case',
	'when',
	'if',
	'elsif',
	'else',
];

export const LIQUID_JS_FILTERS_NAMES = [
	...NATIVE_LIQUID_JS_FILTERS,
	...Object.values(LiquidJsCustomFilters),
];
